// Distribution Gate PR-2 — the B3 per-line review screen for one held servicing
// run at /portfolio-admin/payment-reviews/:paymentId. Shows the full WF1
// allocation trail (fired + zero/skipped lines) built from the disbursement rows
// + run trace, the gate-time rule checks (recipient-mismatch, name-consistency,
// caps, sum, channel, skip reasons — all from persisted data), and the D-13
// recipient corrections. B3 corrects a line's recipient (identity-only; never a
// free-typed amount), recalculates after fixing a datum, then approves or
// rejects. Writes gate on Payments.Review (B3 + ADMIN).
//
// SEAM (PR-6): each line carries its WF1 config attribution (waterfallConfigId +
// position); when the Fee & Obligation Explain engine lands, wire a per-line
// "explain" link to the Waterfall Coach drill-down here. Left un-linked now — the
// drill-down gates on Waterfall.ConfigEdit/Approve which a pure-B3 reviewer may
// not hold, so a live link is deferred with the explain engine (non-blocking).

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  Textarea,
  Input,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import type {
  ServicingAllocationLineDto,
  ServicingReviewCheckDto,
  ServicingReviewCheckSeverity,
} from "@/api/portfolioAdmin/servicingApi";
import {
  useServicingReview,
  useSetRecipientOverride,
  useClearRecipientOverride,
  useRecalculateReview,
  useApproveServicingReview,
  useRejectServicingReview,
} from "@/hooks/portfolioAdmin/useServicing";

const VIEW_PERMISSION = "Payments.Review";

const CHECK_TONE: Record<ServicingReviewCheckSeverity, "ok" | "warn" | "danger" | "neutral"> = {
  ok: "ok",
  warning: "warn",
  error: "danger",
  "not-verified": "neutral",
};

function CheckRow({ check }: { check: ServicingReviewCheckDto }) {
  return (
    <li className="flex items-start gap-space-2 text-sm">
      <StatusBadge tone={CHECK_TONE[check.severity]}>
        {check.severity === "not-verified" ? "not verified" : check.severity}
      </StatusBadge>
      <span className="text-onyx/80">
        {check.position != null ? <span className="text-onyx/50">P{check.position} · </span> : null}
        {check.message}
      </span>
    </li>
  );
}

export default function ServicingReviewDetailPage() {
  const { paymentId = "" } = useParams();
  const { hasPermission, hasRoleCode } = useAuth();
  const toast = useToast();

  const review = useServicingReview(paymentId);
  const loanId = review.data?.loanId ?? "";

  const setOverride = useSetRecipientOverride(paymentId, loanId);
  const clearOverride = useClearRecipientOverride(paymentId, loanId);
  const recalculate = useRecalculateReview(paymentId, loanId);
  const approve = useApproveServicingReview();
  const reject = useRejectServicingReview();

  const [correctLine, setCorrectLine] = useState<ServicingAllocationLineDto | null>(null);
  const [finalRecipient, setFinalRecipient] = useState("");
  const [correctReason, setCorrectReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (!hasPermission(VIEW_PERMISSION) && !hasRoleCode("ADMIN")) {
    return (
      <div className="p-space-6">
        <EmptyState
          variant="empty"
          title="Not authorized"
          description="You don't have access to the payment review screen."
        />
      </div>
    );
  }

  const data = review.data;
  const overridesByPosition = new Map(
    (data?.overrides ?? []).map((o) => [o.priorityPosition, o]),
  );

  const submitCorrection = async () => {
    if (!correctLine) return;
    try {
      await setOverride.mutateAsync({
        priorityPosition: correctLine.position,
        finalRecipientEntityId: finalRecipient.trim(),
        reason: correctReason.trim(),
      });
      toast.success("Recipient corrected — the draft was rebuilt.");
      setCorrectLine(null);
      setFinalRecipient("");
      setCorrectReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const clearCorrection = async (position: number) => {
    try {
      await clearOverride.mutateAsync({ priorityPosition: position, reason: "Cleared by B3 at review." });
      toast.success("Correction cleared — reverted to the resolver's recipient.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const runRecalculate = async () => {
    try {
      await recalculate.mutateAsync();
      toast.success("Recalculated from current data.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const runApprove = async () => {
    try {
      await approve.mutateAsync({ paymentId, loanId });
      toast.success("Approved — the allocation is released for payout.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const runReject = async () => {
    try {
      await reject.mutateAsync({ paymentId, loanId, reason: rejectReason.trim() });
      toast.success("Rejected — the recorded run was unwound.");
      setRejectOpen(false);
      setRejectReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const isHeld = data?.status === "pending_review";

  return (
    <div className="p-space-6 space-y-space-6">
      <PageHeader
        eyebrow="Finance · Payment Reviews"
        title="Review allocation"
        description={
          data
            ? `${data.paymentType} · ${formatCurrency(data.combinedAmount ?? data.amount)}${
                (data.payAheadRuns?.length ?? 0) > 0
                  ? ` · incl. ${data.payAheadRuns!.length} pay-ahead installment${
                      data.payAheadRuns!.length === 1 ? "" : "s"
                    }`
                  : ""
              } · ${formatCurrency(data.combinedTotalDistributed ?? data.totalDistributed)} distributed`
            : "Loading the recorded run…"
        }
      />
      <Link to="/portfolio-admin/payment-reviews" className="text-gold hover:underline text-sm">
        ← Back to the review queue
      </Link>

      {review.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : review.isError || !data ? (
        <EmptyState
          variant="empty"
          title="Couldn't load the run"
          description={extractErrorMessage(review.error)}
          action={{ label: "Try again", onClick: () => void review.refetch() }}
        />
      ) : (
        <>
          {!isHeld ? (
            <div className="rounded-md bg-white-warm p-space-4 text-sm text-onyx/70">
              This run is <strong>{data.status}</strong> — it is no longer awaiting
              review, so corrections and recalculate are unavailable.
            </div>
          ) : null}

          {/* Rule checks */}
          <section>
            <h2 className="text-lg font-semibold text-onyx mb-space-3">Checks</h2>
            {data.checks.length === 0 ? (
              <p className="text-sm text-onyx/60">No checks to report.</p>
            ) : (
              <ul className="space-y-space-2">
                {data.checks.map((c, i) => (
                  <CheckRow key={`${c.code}-${c.position ?? "run"}-${i}`} check={c} />
                ))}
              </ul>
            )}
            <p className="mt-space-2 text-xs text-onyx/50">
              WF1 config:{" "}
              {data.waterfallConfigId
                ? `v${data.waterfallConfigVersion ?? "?"} (${data.waterfallConfigId.slice(0, 8)})`
                : "not attributed (historical/recomputed)"}
            </p>
          </section>

          {/* Per-line allocation table */}
          <section>
            <h2 className="text-lg font-semibold text-onyx mb-space-3">
              Allocation lines
              {(data.payAheadRuns?.length ?? 0) > 0 ? (
                <span className="ml-space-2 text-sm font-normal text-onyx/50">
                  · this period
                </span>
              ) : null}
            </h2>
            <Table>
              <THead>
                <TR>
                  <TH>#</TH>
                  <TH>Obligation type</TH>
                  <TH>Status</TH>
                  <TH>Recipient</TH>
                  <TH>Amount</TH>
                  <TH>Balance</TH>
                  <TH>Channel</TH>
                  <TH>Correction</TH>
                </TR>
              </THead>
              <TBody>
                {data.lines.map((line) => {
                  const ovr = overridesByPosition.get(line.position);
                  const fired = line.status === "fired";
                  return (
                    <TR key={`${line.position}-${line.disbursementId ?? "synthetic"}`}>
                      <TD>{line.position}</TD>
                      <TD>
                        {line.obligationTypeDisplay ?? line.obligationTypeCode}
                        <span className="block text-xs text-onyx/40">{line.obligationTypeCode}</span>
                      </TD>
                      <TD>
                        <StatusBadge tone={fired ? "ok" : "neutral"}>{line.status}</StatusBadge>
                        {line.reason ? (
                          <span className="block text-xs text-onyx/50">{line.reason}</span>
                        ) : null}
                      </TD>
                      <TD>
                        {line.recipientName ?? <span className="text-onyx/40">—</span>}
                        {line.recipientEntityId ? (
                          <span className="block text-xs text-onyx/40">
                            {line.recipientEntityId.slice(0, 8)}
                          </span>
                        ) : null}
                        {ovr ? (
                          <span className="block text-xs text-gold">
                            → {ovr.finalRecipientName ?? ovr.finalRecipientEntityId.slice(0, 8)} · {ovr.reason}
                          </span>
                        ) : null}
                      </TD>
                      <TD>{formatCurrency(line.amount)}</TD>
                      <TD>
                        {line.obligationBalanceBefore != null && line.obligationBalanceAfter != null ? (
                          <span className="text-xs text-onyx/60">
                            {formatCurrency(line.obligationBalanceBefore)} →{" "}
                            {formatCurrency(line.obligationBalanceAfter)}
                          </span>
                        ) : (
                          <span className="text-onyx/40">—</span>
                        )}
                      </TD>
                      <TD>
                        {line.settlementChannel ? (
                          <span className="text-xs text-onyx/60">{line.settlementChannel}</span>
                        ) : (
                          <span className="text-onyx/40">—</span>
                        )}
                      </TD>
                      <TD>
                        {fired && isHeld ? (
                          ovr ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => void clearCorrection(line.position)}
                              disabled={clearOverride.isPending}
                            >
                              Clear
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setCorrectLine(line);
                                setFinalRecipient("");
                                setCorrectReason("");
                              }}
                            >
                              Correct recipient
                            </Button>
                          )
                        ) : null}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </section>

          {/* Pay-ahead installments — the internal multi-run breakdown of this
              record (decision A). Read-only: the whole record is approved/rejected
              as one unit, so children have no per-line correction here. */}
          {(data.payAheadRuns?.length ?? 0) > 0 ? (
            <section data-testid="pay-ahead-runs">
              <h2 className="text-lg font-semibold text-onyx mb-space-1">
                Pay-ahead installments
              </h2>
              <p className="text-sm text-onyx/60 mb-space-3">
                This payment's excess was applied to future installments. Each runs
                through the same waterfall and is released or unwound together with
                the lines above — one record, {formatCurrency(data.combinedAmount ?? data.amount)} total.
              </p>
              <div className="space-y-space-5">
                {data.payAheadRuns!.map((run) => (
                  <div key={run.paymentId} data-testid={`pay-ahead-run-${run.paymentId}`}>
                    <div className="flex items-baseline justify-between mb-space-2">
                      <h3 className="text-sm font-semibold text-onyx">
                        {run.periodStart
                          ? `Advances ${formatCalendarDate(run.periodStart)}`
                          : "Pay-ahead installment"}
                      </h3>
                      <span className="text-sm text-onyx/60">
                        {formatCurrency(run.amount)} ·{" "}
                        {formatCurrency(run.totalDistributed)} distributed
                      </span>
                    </div>
                    <Table>
                      <THead>
                        <TR>
                          <TH>#</TH>
                          <TH>Obligation type</TH>
                          <TH>Status</TH>
                          <TH>Recipient</TH>
                          <TH>Amount</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {run.lines.map((line) => (
                          <TR
                            key={`${run.paymentId}-${line.position}-${line.disbursementId ?? "synthetic"}`}
                          >
                            <TD>{line.position}</TD>
                            <TD>{line.obligationTypeDisplay ?? line.obligationTypeCode}</TD>
                            <TD>
                              <StatusBadge tone={line.status === "fired" ? "ok" : "neutral"}>
                                {line.status}
                              </StatusBadge>
                            </TD>
                            <TD>
                              {line.recipientName ?? <span className="text-onyx/40">—</span>}
                            </TD>
                            <TD>{formatCurrency(line.amount)}</TD>
                          </TR>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* Footer actions */}
          {isHeld ? (
            <div className="flex items-center justify-end gap-space-2">
              <Button
                variant="secondary"
                onClick={() => void runRecalculate()}
                disabled={recalculate.isPending}
              >
                {recalculate.isPending ? "Recalculating…" : "Recalculate"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setRejectOpen(true);
                  setRejectReason("");
                }}
              >
                Reject
              </Button>
              <Button onClick={() => void runApprove()} disabled={approve.isPending}>
                {approve.isPending ? "Approving…" : "Approve and release"}
              </Button>
            </div>
          ) : null}
        </>
      )}

      {/* Correct-recipient modal (identity-only, D-13) */}
      <Modal
        open={correctLine != null}
        onClose={() => setCorrectLine(null)}
        title="Correct the recipient"
      >
        <div className="space-y-space-4">
          <p className="text-sm text-onyx/70">
            Re-route this line to a different payment-engine counterparty. This is
            identity-only — the amount does not change (D-13). To change an amount,
            fix the underlying datum (fee config, broker link, split percent) and
            Recalculate.
          </p>
          <label className="block text-sm">
            <span className="text-onyx/70">Corrected recipient (Counterparty ID)</span>
            <Input
              value={finalRecipient}
              onChange={(e) => setFinalRecipient(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </label>
          <Textarea
            value={correctReason}
            onChange={(e) => setCorrectReason(e.target.value)}
            placeholder="Why is the recipient being corrected?"
            rows={3}
          />
          <div className="flex justify-end gap-space-2">
            <Button variant="secondary" onClick={() => setCorrectLine(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => void submitCorrection()}
              disabled={
                setOverride.isPending ||
                finalRecipient.trim().length === 0 ||
                correctReason.trim().length === 0
              }
            >
              {setOverride.isPending ? "Applying…" : "Apply correction"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject this recorded payment?">
        <div className="space-y-space-4">
          <p className="text-sm text-onyx/70">
            Rejecting unwinds the recorded run — its disbursements and obligation
            balances are restored. Nothing has been distributed, so there's no
            claw-back. A reason is required.
          </p>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Why is this being rejected?"
            rows={3}
          />
          <div className="flex justify-end gap-space-2">
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void runReject()}
              disabled={reject.isPending || rejectReason.trim().length === 0}
            >
              {reject.isPending ? "Rejecting…" : "Reject and unwind"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
